import { type Permission } from '@convo/core';
import { create } from 'zustand';

import {
  authApi,
  type AuthUser,
  type RegisterInput,
  type SessionResponse,
  type WorkspaceSummary,
} from '../api/auth';
import { ApiError, setAuthBridge } from '../api/http';
import { logError, logInfo } from '../lib/logger';
import { storage } from '../lib/storage';

/**
 * 登入狀態。
 *
 * **access token 只放在記憶體裡**，不進 localStorage：能被 JS 讀到的地方，XSS 就讀得到。
 * 重新整理之後靠 httpOnly 的 refresh cookie 換一張新的（bootstrap()），所以使用者
 * 不會被登出，但攻擊者也偷不到長效憑證。
 *
 * localStorage 只存「上次看的是哪個 workspace」——那不是機密，而且沒有它每次重整
 * 都會跳回第一個 workspace。它同時兼任「這台瀏覽器有沒有 session」的提示：
 * 只有 applySession 會寫、只有 reset 會清，跟 refresh cookie 同生共死。
 */

const LAST_WORKSPACE_KEY = 'convo.last_workspace';

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  user: AuthUser | null;
  workspace: WorkspaceSummary | null;
  workspaces: WorkspaceSummary[];
  permissions: Permission[];
  /** 最近一次被拒絕的原因，讓 UI 顯示「權限不足」而不是白畫面。 */
  forbiddenMessage: string | null;

  bootstrap: () => Promise<void>;
  /** 清成未登入。登出、token 失效、測試之間都用它。 */
  reset: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  /**
   * 重新拉一次「我是誰、我在哪些 workspace」。
   *
   * 改完個人資料或接受邀請之後要用：側欄的名字與 workspace 切換器都讀這份狀態，
   * 不更新的話畫面會停在舊資料，使用者會以為剛才的操作沒生效。
   */
  refreshSession: () => Promise<void>;
  clearForbidden: () => void;
  can: (permission: Permission) => boolean;
}

/**
 * bootstrap 的去重。
 *
 * React 19 的 StrictMode 會在 dev 把 effect 跑兩次；refresh token 是 rotation 的，
 * 打兩次的話第二次一定失敗，使用者就被登出了。同一個 promise 共用給所有呼叫者。
 */
let bootstrapPromise: Promise<void> | null = null;

const ANONYMOUS = {
  status: 'anonymous' as const,
  accessToken: null,
  user: null,
  workspace: null,
  workspaces: [] as WorkspaceSummary[],
  permissions: [] as Permission[],
};

/** 解構這個 store 時務必搭配 useShallow。 */
export const useAuthStore = create<AuthState>((set, get) => ({
  ...ANONYMOUS,
  status: 'unknown',
  forbiddenMessage: null,

  bootstrap() {
    bootstrapPromise ??= (async () => {
      try {
        const lastWorkspace = storage.get(LAST_WORKSPACE_KEY);

        /**
         * 沒有那把 workspace key＝這台瀏覽器沒登入過（或已登出），refresh 注定 400。
         * 直接當訪客，省下一次跨網域請求——landing 的讀者絕大多數是這一類，
         * 而那個 400 會在他們的 console 留一條紅字。
         *
         * storage 整個不能用時要照打：那時候「讀不到」不等於「沒有」，
         * 誤判會讓 Safari 無痕的使用者每次重整都被登出。
         */
        if (lastWorkspace === null && storage.isAvailable()) {
          set({ ...ANONYMOUS });
          return;
        }

        const session = await authApi.refresh(lastWorkspace ?? undefined);
        applySession(set, session);

        // 切換器需要完整清單，登入回應只帶目前這個
        const me = await authApi.me();
        set({ workspaces: me.workspaces });
      } catch (error) {
        // 沒有有效的 cookie 就是還沒登入，那是正常狀況，不用當錯誤記
        if (!(error instanceof ApiError)) {
          logError('bootstrap 失敗', error);
        }
        set({ ...ANONYMOUS });
      } finally {
        bootstrapPromise = null;
      }
    })();

    return bootstrapPromise;
  },

  async login(email, password) {
    const remembered = storage.get(LAST_WORKSPACE_KEY) ?? undefined;
    let session: SessionResponse;

    try {
      session = await authApi.login(email, password, remembered);
    } catch (error) {
      /**
       * 記住的只是「上次在哪」：那個 workspace 被刪掉、或自己被移出之後，指定它登入一律 403
       * （密碼錯是 401，走不到這裡）。一直帶著的話，這台瀏覽器之後每一次登入都會撞同一道牆
       * ——密碼對了也進不去。不指定再登一次，後端給第一個還在的；applySession 會把記憶改寫掉。
       */
      if (remembered === undefined || !(error instanceof ApiError) || !error.isForbidden) {
        throw error;
      }
      session = await authApi.login(email, password);
    }

    applySession(set, session);
    // 全新的 session：之前那一次 403（或 bootstrap 撞到的）在 http 層留下的「權限不足」不再適用
    set({ forbiddenMessage: null });

    const me = await authApi.me();
    set({ workspaces: me.workspaces });
  },

  async register(input) {
    const session = await authApi.register(input);
    applySession(set, session);
    set({ workspaces: [session.workspace] });
  },

  async logout() {
    try {
      await authApi.logout();
    } catch (error) {
      // 後端撤銷失敗也要把本地狀態清掉，不然使用者會卡在「看起來還登入著」
      logError('登出請求失敗，仍清除本地狀態', error);
    }
    get().reset();
  },

  reset() {
    // 還在飛的 bootstrap 也要放掉，不然清完狀態之後它回來又把舊 session 寫回去
    bootstrapPromise = null;
    storage.remove(LAST_WORKSPACE_KEY);
    set({ ...ANONYMOUS, forbiddenMessage: null });
  },

  async refreshSession() {
    const me = await authApi.me();
    set({ user: me.user, workspaces: me.workspaces });
  },

  async switchWorkspace(workspaceId) {
    const session = await authApi.switchWorkspace(workspaceId);
    applySession(set, session as SessionResponse);
    logInfo('切換 workspace', { workspaceId });
  },

  clearForbidden() {
    set({ forbiddenMessage: null });
  },

  can(permission) {
    return get().permissions.includes(permission);
  },
}));

function applySession(set: (partial: Partial<AuthState>) => void, session: SessionResponse): void {
  storage.set(LAST_WORKSPACE_KEY, session.workspace.id);
  set({
    status: 'authenticated',
    accessToken: session.accessToken,
    user: session.user,
    workspace: session.workspace,
    permissions: session.permissions,
  });
}

/**
 * 把 store 接到 http 層。main.tsx 啟動時呼叫一次。
 *
 * 這裡刻意用 `useAuthStore.getState()` 而不是 hook：http 層不在 React 的世界裡，
 * 訂閱沒有意義，每次都拿當下的值才對。
 */
export function installAuthBridge(): void {
  setAuthBridge({
    getAccessToken: () => useAuthStore.getState().accessToken,

    refresh: async () => {
      try {
        const session = await authApi.refresh(useAuthStore.getState().workspace?.id);
        applySession(useAuthStore.setState, session);
        return session.accessToken;
      } catch {
        return null;
      }
    },

    onAuthLost: () => {
      useAuthStore.getState().reset();
    },

    onForbidden: (error) => {
      useAuthStore.setState({ forbiddenMessage: error.message });
    },
  });
}
